import Cookies from 'js-cookie';
import { PostHogFeatureFlags } from '@lib/posthog-feature-flags';

/**
 * Shared contract for the feature-flag bootstrap cookie.
 *
 * The middleware evaluates the `web-*` PostHog flags once per instance every
 * 30 seconds (see lib/analytics/posthog-flags-edge.ts) and delivers the values
 * on every HTML response in this cookie. The client reads it synchronously at
 * init and feeds it into `posthog.init({ bootstrap })`, so flags are correct
 * at first paint and the SDK's per-pageload `/flags` request can be skipped
 * (`advanced_disable_feature_flags_on_first_load`). Previously every hard load
 * fired one `/flags` request (two for members, whose identify() reloads), which
 * at ~700k sessions a week was millions of billable flag requests a month spent
 * recomputing the same site-wide toggles.
 *
 * This is a functional cookie in the same category as `user-dnt` and
 * `geo-currency`: it carries site configuration, not identity, so it is not
 * gated on analytics consent. Flag evaluation is deliberately consent-exempt
 * (see initPostHog in lib/posthog.ts).
 *
 * That means it must be registered in CookieYes's `necessary` category, as
 * `geo-currency` is: CookieYes deletes anything outside `necessary` about a
 * second after load, which for a cookie the middleware rewrites on every HTML
 * response means per-page churn for non-consenting visitors and an
 * uncategorised entry in the CookieYes scan. FFH-2501 was the same problem on
 * `geo-currency`, where the deletion showed up as a price flash.
 */

export const FLAGS_BOOTSTRAP_COOKIE = 'ph_flags';

/**
 * Short-lived on purpose: the middleware refreshes the cookie on every HTML
 * response, so the max-age only matters when the middleware stops delivering
 * it (an outage, or PostHog unreachable). Five minutes lets a mid-outage hard
 * load reuse the last known values, after which the hooks' defaults take over.
 */
export const FLAGS_BOOTSTRAP_COOKIE_MAX_AGE_SECONDS = 300;

export interface PostHogFlagsBootstrap {
  /** Flag key to enabled state (or variant key for multivariate flags). */
  featureFlags: Record<string, boolean | string>;
  /** Flag key to decoded JSON payload, present only for enabled flags. */
  featureFlagPayloads: Record<string, unknown>;
}

export const serialiseFlagsBootstrap = (
  bootstrap: PostHogFlagsBootstrap
): string => JSON.stringify(bootstrap);

const isFlagValueRecord = (
  value: unknown
): value is Record<string, boolean | string> =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.values(value).every(
    (flagValue) =>
      typeof flagValue === 'boolean' || typeof flagValue === 'string'
  );

const isPayloadRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * The middleware writes an explicit entry for every key in
 * `PostHogFeatureFlags` (see toBootstrap in posthog-flags-edge.ts), so a
 * cookie missing any of them did not come from this build: it was either
 * tampered with, or written before a deploy that added a flag. `in` rather
 * than a truthiness check, because a disabled flag is present as `false`.
 */
const hasEveryFlagKey = (
  featureFlags: Record<string, boolean | string>
): boolean =>
  Object.values(PostHogFeatureFlags).every(
    (flagKey) => flagKey in featureFlags
  );

/**
 * The values are fed straight into `posthog.init({ bootstrap })`, so a junk or
 * tampered cookie must be rejected rather than trusted, mirroring the UUID
 * check on the anonymous-identity cookie (lib/analytics/posthog-identity.ts).
 * Returning undefined makes the caller fall back to a live `/flags` request,
 * so a bad cookie degrades to the pre-bootstrap behaviour, never to junk flags.
 *
 * Completeness matters as much as shape here: any accepted value sets
 * `advanced_disable_feature_flags_on_first_load` in lib/posthog.ts, which
 * suppresses the init-time request. A cookie that parses but omits keys would
 * leave those flags on their call-site defaults until a member's identify(),
 * and forever for anonymous visitors, so a partial record is rejected
 * outright in favour of the live request.
 */
export const readFlagsBootstrapCookie = ():
  | PostHogFlagsBootstrap
  | undefined => {
  if (typeof window === 'undefined') return undefined;

  const rawValue = Cookies.get(FLAGS_BOOTSTRAP_COOKIE);
  if (!rawValue) return undefined;

  try {
    const parsed: unknown = JSON.parse(rawValue);
    if (typeof parsed !== 'object' || parsed === null) return undefined;

    const { featureFlags, featureFlagPayloads } =
      parsed as PostHogFlagsBootstrap;
    if (
      !isFlagValueRecord(featureFlags) ||
      !hasEveryFlagKey(featureFlags) ||
      !isPayloadRecord(featureFlagPayloads)
    ) {
      return undefined;
    }

    // Rebuilt rather than returned as parsed, so the validated shape is the
    // whole returned value. initPostHog already picks the two fields
    // explicitly, so this is defence in depth at the parse boundary rather
    // than a fix for a live path: it keeps a tampered cookie's extra keys
    // (a `distinctID`, say) from reaching any future caller that spreads
    // this wholesale into posthog.init({ bootstrap }).
    return { featureFlags, featureFlagPayloads };
  } catch {
    return undefined;
  }
};
